import {RepositoryPickerSearchRepositoriesServerQuery} from '@github-ui/server-persisted-queries'
import type {RepositoryPickerSearchRepositoriesServerQueryQuery} from '@github-ui/server-persisted-queries/types/RepositoryPickerSearchRepositoriesServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {RepositoryPickerRepository} from '../repository-picker-types'
import {SharedRequestManager} from '../shared-request-manager'

export type RepositorySearchConnection = RepositoryPickerSearchRepositoriesServerQueryQuery['search']

export function isValidSearchConnection(value: unknown): value is {
  repositoryCount: number
  pageInfo: {hasNextPage: boolean; endCursor: string | null}
  nodes: unknown[] | null
} {
  if (!value || typeof value !== 'object') return false

  const candidate = value as {
    repositoryCount?: unknown
    pageInfo?: unknown
    nodes?: unknown
  }

  if (typeof candidate.repositoryCount !== 'number') return false
  if (!candidate.pageInfo || typeof candidate.pageInfo !== 'object') return false

  const pageInfo = candidate.pageInfo as {hasNextPage?: unknown; endCursor?: unknown}
  if (typeof pageInfo.hasNextPage !== 'boolean') return false
  if (!(pageInfo.endCursor === null || typeof pageInfo.endCursor === 'string')) return false
  if (!(candidate.nodes == null || Array.isArray(candidate.nodes))) return false

  return true
}

export async function fetchSearchRepositoriesFromServer(
  searchQuery: string,
  signal?: AbortSignal,
  after?: string,
): Promise<RepositorySearchConnection> {
  const query = new URLSearchParams({
    queryId: RepositoryPickerSearchRepositoriesServerQuery,
    searchQuery,
  })
  if (after) query.set('after', after)

  const response = await reactFetchJSON(`/_ghui/item-pickers/repositories_search_full?${query.toString()}`, {signal})
  if (!response.ok) throw new Error(`Repository full search failed (${response.status})`)

  const json = (await response.json()) as {
    data?: RepositoryPickerSearchRepositoriesServerQueryQuery
    errors?: unknown[]
  }
  if (!isValidSearchConnection(json.data?.search)) {
    throw new Error('Repository full search query returned invalid data')
  }

  return json.data.search
}

const sharedSearchRequests = new SharedRequestManager<RepositorySearchConnection>()

export function fetchSharedSearchRepositories(searchQuery: string, signal: AbortSignal, after?: string) {
  // The cursor is part of the identity of a page, so it has to be part of the dedupe key.
  // `\u0000` cannot appear in a search query, so it is safe as a separator.
  return sharedSearchRequests.fetch(`${searchQuery}\u0000${after ?? ''}`, signal, requestSignal =>
    fetchSearchRepositoriesFromServer(searchQuery, requestSignal, after),
  )
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

export type PagedRepositorySearchOptions = {
  searchQuery: string
  signal: AbortSignal
  /** How many eligible repositories to collect before stopping. */
  limit: number
  /** Upper bound on cursor pages requested, so a sparse result set cannot page indefinitely. */
  maxPages: number
  /**
   * `owner/name` the search text names exactly. Paging continues past {@link limit} until this
   * repository is found or the pages run out, which is what makes an exact repository reachable
   * when relevance alone does not place it on the first page.
   */
  exactNameWithOwner?: string
  isEligible?: (repository: RepositoryPickerRepository) => boolean
}

export type PagedRepositorySearchResult = {
  repositories: RepositoryPickerRepository[]
  /** True when paging stopped with pages still available, so the caller can say so in the UI. */
  hasMore: boolean
  pagesFetched: number
}

/**
 * Walks the search connection's cursors, collecting eligible repositories.
 *
 * The underlying GraphQL search returns relevance-ordered results, so the first page is usually
 * enough. Paging matters for the case relevance alone does not cover: an exact `owner/name` that
 * shares a name prefix with many other repositories, where the exact match can land beyond the
 * first page.
 */
export async function searchRepositoriesPaged({
  searchQuery,
  signal,
  limit,
  maxPages,
  exactNameWithOwner,
  isEligible,
}: PagedRepositorySearchOptions): Promise<PagedRepositorySearchResult> {
  const wanted = exactNameWithOwner?.toLowerCase()
  const repositories: RepositoryPickerRepository[] = []
  const seen = new Set<string>()
  let after: string | undefined
  let pagesFetched = 0
  let hasMore = false
  let foundExact = wanted == null

  while (pagesFetched < maxPages) {
    let page: RepositorySearchConnection
    try {
      page = await fetchSharedSearchRepositories(searchQuery, signal, after)
    } catch (error) {
      // A failed first page has nothing to fall back on, and an aborted search is never partial.
      // A later page failing degrades to the repositories already collected.
      if (isAbortError(error) || pagesFetched === 0) throw error
      hasMore = true
      break
    }
    pagesFetched += 1

    for (const node of page.nodes ?? []) {
      if (node?.__typename !== 'Repository') continue
      const repository = node as RepositoryPickerRepository
      if (seen.has(repository.id)) continue
      if (isEligible && !isEligible(repository)) continue
      seen.add(repository.id)
      repositories.push(repository)
      if (wanted != null && repository.nameWithOwner.toLowerCase() === wanted) foundExact = true
    }

    const canContinue = page.pageInfo.hasNextPage && Boolean(page.pageInfo.endCursor)
    hasMore = canContinue
    if (!canContinue) break
    if (repositories.length >= limit && foundExact) break

    after = page.pageInfo.endCursor ?? undefined
  }

  return {repositories, hasMore, pagesFetched}
}
